import React, { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { notification } from "antd";
import { usePlaidLink } from "react-plaid-link";
import { exchangePlaidToken } from "../../_redux/banks/BanksCrud";
import { handleApiErrorResponse } from "../../../Common/utils";
import { loadPlaidOAuthState, clearPlaidOAuthState } from "./plaidOAuthStorage";

const BANKING_PATH = "/accounting/banking";

// Landing page for Plaid's OAuth redirect. The bank returns the browser here
// after authentication; we re-open Link with the original token so it can
// finish, then exchange the resulting public token.
export default function PlaidOAuthReturn() {
  const history = useHistory();
  const { linkToken, plaidItemId } = loadPlaidOAuthState();

  const goBack = useCallback(() => {
    history.replace(BANKING_PATH);
  }, [history]);

  const handleSuccess = useCallback(
    async (publicToken) => {
      try {
        // plaid_item_id marks this as a reconnect, so the server repairs the existing
        // connection (keeping its cursor) instead of treating it as a new bank.
        const payload = { public_token: publicToken, ...(plaidItemId ? { plaid_item_id: plaidItemId } : {}) };
        await exchangePlaidToken(payload);
        notification.success({
          message: plaidItemId ? "Bank connection restored" : "Bank account connected successfully",
        });
      } catch (err) {
        handleApiErrorResponse(err);
      } finally {
        clearPlaidOAuthState();
        goBack();
      }
    },
    [goBack, plaidItemId]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    receivedRedirectUri: window.location.href,
    onSuccess: handleSuccess,
    onExit: (err) => {
      if (err) {
        notification.error({ message: "Bank connection was cancelled" });
      }
      clearPlaidOAuthState();
      goBack();
    },
  });

  // Reaching this page without a stored token means it was not an OAuth handoff.
  useEffect(() => {
    if (!linkToken) {
      goBack();
    }
  }, [linkToken, goBack]);

  // Resume Link to complete the OAuth handoff as soon as it is ready.
  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  return <div style={{ padding: 24 }}>Completing bank connection…</div>;
}
